import {
  Box, Grid, Stack, Typography, useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  ArrowRight, BookOpen,
  Brain,
  Building2,
  Earth, Globe, GraduationCap, Handshake,
  Leaf, MessageCircleMore,
  Network,
  Shield,
  Smartphone,
  TrendingUp, User, Users
} from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import {
  ArchitectureCard, ArchitectureCardArrowSeparator,
  ArchitectureCardIconContainer, ArchitectureListContainer,
  Container,
  DescriptionPlayerContainer, FacultyCard,
  FacultyCardContent, FacultyCardOverlay,
  //GovernanceCard, GovernanceCardIndexContainer, GovernanceListContainer,
  Header, ValueCard, ValuesContainer
} from "./Academy.styles";
import { Modal, Spinner } from "../../components";
/*import AcademyPlayer from "../../components/AcademyPlayer/AcademyPlayer.component";*/
import { useAnnouncement } from "../../components/Announcement/Announcement.component";
import { Button } from "../../components/Button/Button.component";
import { LEVEL } from "../../consts";
import { useStartOrResumeQuery } from "../../queries";
import { useNavigate } from "../../services";
import { useAuthStore, useLanguageStore } from "../../stores";
import { getError } from "../../utils/error";
const facultiesList = [
  { title: "academy.culture_management", description: "academy.culture_management_description", icon: Building2, background: "https://static.openlearning-test.digitedacademy.net/media/cultura manageriale.png" },
  { title: "academy.ai_data", description: "academy.ai_data_description", icon: Brain, background: "https://images.unsplash.com/photo-1697577418970-95d99b5a55cf" },
  { title: "academy.network_development", description: "academy.network_development_description", icon: Network, background: "https://images.unsplash.com/photo-1758304480414-a78219c4e121" },
  { title: "academy.wealth_management", description: "academy.wealth_management_description", icon: TrendingUp, background: "https://images.unsplash.com/photo-1705948734578-54b4e4dbf443" },
  { title: "academy.esg", description: "academy.esg_description", icon: Leaf, background: "https://images.unsplash.com/photo-1607369165516-0e831913b397" },
  { title: "academy.risk_culture", description: "academy.risk_culture_description", icon: Shield, background: "https://images.unsplash.com/photo-1644093387522-b1be6de270a5" },
  { title: "academy.internalizzazione", description: "academy.internalizzazione_description", icon: Earth, background: "https://static.openlearning-test.digitedacademy.net/media/internazionalizzazione.png" },
  { title: "academy.trainers_path", description: "academy.trainers_path_description", icon: Smartphone, background: "https://images.unsplash.com/photo-1758524572081-5a84e02768ef" }
];

const valuesList = [
  { title: "academy.back_to_class", description: "academy.back_to_class_description", icon: GraduationCap },
  { title: "academy.personalizations", description: "academy.personalizations_description", icon: User },
  { title: "academy.dialogue", description: "academy.dialogue_description", icon: MessageCircleMore },
  { title: "academy.partnership", description: "academy.partnership_description", icon: Handshake },
  { title: "academy.internalization", description: "academy.internalization_description", icon: Globe }
];

const architectureList = [
  { title: "academy.academy", description: "academy.academy_description", icon: Building2 },
  { title: "academy.faculties", description: "academy.faculty_description", icon: Users },
  { title: "academy.programs", description: "academy.programs_description", icon: BookOpen }
];

const isNotFoundCode = "core-005";

export function Academy() {

  const { t } = useTranslation();
  const theme = useTheme();
  const { announce } = useAnnouncement();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);

  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  setSpinnerLoginVisible(false);

  const homeLink = {
    label: smartConfiguration?.visForyou === false ? "Esplora" : "Per Te",
    path: smartConfiguration?.visForyou === false ? "/esplora" : "/per-te"
  };

  const { id: idString } = useParams<{
    id: string
  }>();

  const idNumber = idString ? Number(idString) : NaN;
  const language = useLanguageStore(state => state.language)?.id;

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const parentId = searchParams.get("parentId") ?? "";
  const grandParentId = searchParams.get("grandParentId") ?? "";

  const {
    data: learningObject,
    error:learningObjectError,
    isLoading: learningObjectIsFetching,
    isError:learningObjectIsError
  } = useStartOrResumeQuery({
    grandParentId,
    language,
    learningObjectId: idNumber,
    learningObjectTypology: LEVEL.DA_URL_RES,
    parentId
  });

  const hasWrongType = useMemo(() => {
    if(learningObjectIsFetching || !learningObject?.learningObjectTypology) return false;
    return learningObject.learningObjectTypology !== LEVEL.DA_URL_RES;
  }, [learningObjectIsFetching, learningObject]);


  const isNotFound = getError(learningObjectError)?.[0]?.cause === isNotFoundCode;
  const isNotEnrolled = getError(learningObjectError)?.[0]?.cause === "STUDENT_NOT_ENROLLED";

  if(isNotFound || hasWrongType){
    navigate("/not-found");
  }

  if (isNotEnrolled){
    navigate("/not-found", { state: { "message": t("student.not.enrolled") } });
  }

  useEffect(() => {
    if(learningObjectError && !isNotFound && !showModal) setShowModal(true);
  }, [learningObjectError, isNotFound, showModal]);

  useEffect(() => {
    announce( t("announcement_academy"));
  }, []);

  if(learningObjectIsFetching) return (
    <Box role="status" aria-label={ t("loading") } sx={ {
      paddingTop: "32px",
      paddingBottom: "32px"
    } }>
      <Spinner />
    </Box>
  );

  return (
    <>
      <Header role="banner" theme={ theme }>
        <Container $isMobile={ isMobile }>
          <Button onClick={ () => navigate(homeLink.path) } startIcon={ <ArrowLeft aria-hidden={ true } color={ theme.customColors.systemSecondary01 }/> } variant={ "text" }>
            <Typography color={ theme.customColors.systemSecondary01 }>Indietro</Typography>
          </Button>
        </Container>
      </Header>

      { /*
      <AcademyPlayer url={ "https://static.openlearning-isp.digitedacademy.net/media/Academy4Future.mp4" } height={ isMobile ? "380px" : "710px" }/>*/ }

      <Box component="main" aria-labelledby="academy-description-heading">

        { /*Descrizione*/ }
        <Container component="section" aria-labelledby="academy-description-heading" sx={ { backgroundColor: theme.customColors.backgroundPrimary } } $isMobile={ isMobile }>
          <Box sx={ {
            paddingY: "95px"
          } }>
            <Stack direction={ isMobile ? "column" : "row" } alignItems={ "center" } gap={ "48px" }>
              <Stack direction={ "column" } gap={ "24px" } sx={ {
                width: isMobile ? "100%" : "40%",
                textAlign: isMobile ? "center" : "left"
              } }>
                <Typography id="academy-description-heading" component={ "h1" } fontSize={ 28 } fontWeight={ 700 }>{ t("academy.corporate_group") }</Typography>
                <Typography fontSize={ 16 }>{ t("academy.description_1") }</Typography>
                <Typography fontSize={ 16 }>{ t("academy.description_2") }</Typography>
              </Stack>

              <DescriptionPlayerContainer $isMobile={ isMobile }>
                <img src={ "https://static.openlearning-isp.digitedacademy.net/media/academyImage.png" } alt={ t("academy.description_1") } role="presentation" loading="lazy" style={ {
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  height: "100%",
                  display: "block"
                } }/>
              </DescriptionPlayerContainer>
            </Stack>
          </Box>
        </Container>

        { /*Architettura*/ }
        <Box component="section" aria-labelledby="academy-architecture-heading" sx={ { backgroundColor: theme.customColors.backgroundFooter } }>
          <Container $isMobile={ isMobile }>
            <Box sx={ {
              paddingY: "95px"
            } }>
              <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "64px" }>
                <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "12px" } sx={ {
                  textAlign: isMobile ? "center" : "left"
                } }>
                  <Typography id="academy-architecture-heading" component={ "h2" } fontSize={ 28 } fontWeight={ 700 }>{ t("academy.title_architecture") }</Typography>
                  <Typography component={ "p" } fontSize={ 16 }>{ t("academy.subtitle_architecture") }</Typography>
                </Stack>

                <ArchitectureListContainer role="list" theme={ theme }>
                  { architectureList.map((architecture, index) => {

                    const Icon = architecture.icon;

                    return (
                      <Fragment key={ `architecture_card_${index}` }>
                        <ArchitectureCard role="listitem" theme={ theme } $isFirst={ false }>
                          <ArchitectureCardIconContainer theme={ theme }>
                            <Icon aria-hidden={ true } size={ 48 } color={ theme.customColors.backgroundPrimaryCta } />
                          </ArchitectureCardIconContainer>
                          <Box sx={ { height:"15rem" } }>
                            <Typography component={ "h3" } textAlign={ "center" } fontSize={ 16 } fontWeight={ 600 } sx={ { color: theme.customColors.textPrimary } }>{ t(architecture.title) }</Typography>
                            <Typography textAlign={ "center" } fontSize={ 14 } fontWeight={ 400 } sx={ { color: theme.customColors.textPrimary } } dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize(t(architecture.description)) } }/>
                          </Box>
                        </ArchitectureCard>

                        { (index < architectureList.length -1) && (
                          <ArchitectureCardArrowSeparator aria-hidden={ true } theme={ theme }>
                            <ArrowRight aria-hidden={ true } size={ 48 } color={ theme.customColors.backgroundPrimaryCta }/>
                          </ArchitectureCardArrowSeparator>
                        ) }
                      </Fragment>
                    );
                  }) }
                </ArchitectureListContainer>
              </Stack>
            </Box>
          </Container>
        </Box>

        { /*Faculties*/ }
        <Container component="section" aria-labelledby="academy-faculties-heading" sx={ { backgroundColor: theme.customColors.backgroundPrimary } } $isMobile={ isMobile }>
          <Box sx={ {
            paddingY: "95px"
          } }>
            <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "64px" }>
              <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "12px" }>
                <Typography id="academy-faculties-heading" component={ "h2" } fontSize={ 28 } fontWeight={ 700 }>{ t("academy.title_faculties") }</Typography>
                <Typography component={ "p" } fontSize={ 16 } fontWeight={ 700 }>{ t("academy.subtitle_faculties") }</Typography>
              </Stack>

              <Grid container role="list" spacing={ "32px" }>
                { facultiesList.map((faculty, index) => {
                  const Icon = faculty.icon;
                  return (
                    <Grid role="listitem" key={ `faculty_${index}` } item xs={ 12 } sm={ 6 } md={ 4 }>
                      <FacultyCard theme={ theme }>
                        <img src={ faculty.background } alt={ t(faculty.title) } role="presentation" style={ { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" } } loading="lazy"/>
                        <FacultyCardOverlay aria-hidden={ true } theme={ theme }/>
                        <FacultyCardContent theme={ theme }>
                          <Stack direction={ "column" } justifyContent={ "end" } alignItems={ "start" } gap={ "13px" } height={ "100%" } padding={ "28px 24px" }>
                            <Icon aria-hidden={ true } size={ 32 } />
                            <Typography variant={ "h3" } fontSize={ 16 } fontWeight={ 700 }>{ t(faculty.title) }</Typography>
                            <Typography fontSize={ 14 } dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize(t(faculty.description)) } }/>
                          </Stack>
                        </FacultyCardContent>
                      </FacultyCard>
                    </Grid>
                  );
                }) }
                { /* card da poter mostrare se ci sono elementi incoming
              <Grid item xs={ 12 } sm={ 6 } md={ 4 }>
                <FacultyCard theme={ theme }>
                  <img src={ "https://images.unsplash.com/photo-1712331393873-f671bec94e21" } alt={ "Coming soon" } style={ { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" } } loading="lazy"/>
                  <FacultyCardOverlay theme={ theme }/>
                  <FacultyCardContent theme={ theme }>
                    <Typography variant={ "h3" } fontSize={ 22 } fontWeight={ 700 }>{ "Coming Soon" }</Typography>
                  </FacultyCardContent>
                </FacultyCard>
              </Grid>
             */ }
              </Grid>
            </Stack>
          </Box>
        </Container>

        { /*DNA*/ }
        <Box component="section" aria-labelledby="academy-dna-heading" sx={ {
          backgroundColor: theme.customColors.backgroundFooter
        } }>
          <Container $isMobile={ isMobile }>
            <Box sx={ {
              paddingY: "95px"
            } }>
              <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "64px" }>
                <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "12px" } sx={ {
                  textAlign: isMobile ? "center" : "left"
                } }>
                  <Typography id="academy-dna-heading" component={ "h2" } fontSize={ 28 } fontWeight={ 700 }>{ t("academy.dna") }</Typography>
                  <Typography component={ "p" } fontSize={ 16 } fontWeight={ 600 }>{ t("academy.university_values") }</Typography>
                </Stack>
                <ValuesContainer role="list" theme={ theme } >
                  { valuesList.map((value, index) => {
                    const Icon = value.icon;
                    return (
                      <ValueCard role="listitem" key={ `value_${index}` } theme={ theme } $isMobile={ isMobile }>
                        <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "24px" }>
                          <Icon aria-hidden={ true } size={ 48 } color={ theme.customColors.backgroundPrimaryCta }/>
                          <Typography variant={ "h3" } fontSize={ 20 } fontWeight={ 700 }>{ t(value.title) }</Typography>
                          <Typography fontSize={ 14 }>{ t(value.description) }</Typography>
                        </Stack>
                      </ValueCard>
                    );
                  }) }
                </ValuesContainer>
              </Stack>
            </Box>
          </Container>
        </Box>

        { /*Altro*/ }
        <Container sx={ { backgroundColor: theme.customColors.backgroundPrimary } }$isMobile={ isMobile }>
          <Box sx={ {
            paddingY: "95px",
            textAlign: "center"
          } }>
            <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "48px" }>
              <Button onClick={ () => navigate(homeLink.path) } variant={ "contained" } endIcon={ <ArrowRight aria-hidden={ true }/> }>{ t("academy.go_to_page") } { homeLink.label }</Button>
              <Typography fontSize={ 12 }>{ t("academy.contents_for_you") }</Typography>
            </Stack>
          </Box>
        </Container>

      </Box> { /* chiusura <main> */ }

      <Modal
        description={ getError(learningObjectError)?.[0]?.message ?? "" }
        open={ learningObjectIsError && showModal }
        title={ getError(learningObjectError)?.[0]?.name ?? "" }
        onClose={ ()=> {
          setShowModal(false);
          navigate(-1);
        } }
      />
    </>
  );
}
